// src/pages/permit-agent/ChatWindow.tsx
import React, { useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import CloudDownload from '@material-ui/icons/CloudDownload';
import { FileCopy } from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import { ChatWindowProps } from '../../types/deep-research';
import { IMAGE_MIME_TYPES } from '../../utils/fileAccept';

const ChatWindow: React.FC<ChatWindowProps> = ({ chatData, isLoading, onStartResearch }) => {
  const chatListRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const prevChatDataLengthRef = useRef<number>(0);
  const prevIsLoadingRef = useRef<boolean>(false);
  const lastContentRef = useRef<string>('');

  // Scroll function that finds and scrolls the scrollable parent
  const performScroll = useCallback(() => {
    if (chatListRef.current) {
      // Find scrollable parent and scroll it to bottom
      let scrollableParent = chatListRef.current.parentElement;
      while (scrollableParent && scrollableParent !== document.body) {
        const style = window.getComputedStyle(scrollableParent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflow === 'auto' || style.overflow === 'scroll') {
          // Capture the element reference to avoid closure issues
          const elementToScroll = scrollableParent;
          // Use requestAnimationFrame and setTimeout to ensure DOM has fully updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              elementToScroll.scrollTop = elementToScroll.scrollHeight;
            }, 0);
          });
          break;
        }
        scrollableParent = scrollableParent.parentElement;
      }
    }
    // Also try scrolling the last message into view as a fallback
    if (lastMessageRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 0);
      });
    }
  }, []);

  // Track changes and scroll when needed - runs on every render to catch all changes
  useLayoutEffect(() => {
    const currentLength = chatData.length;
    const lastMessage = chatData[currentLength - 1];
    const lastContent = lastMessage?.content || '';
    const shouldScroll =
      currentLength !== prevChatDataLengthRef.current ||
      isLoading !== prevIsLoadingRef.current ||
      lastContent !== lastContentRef.current;

    if (shouldScroll) {
      performScroll();
      prevChatDataLengthRef.current = currentLength;
      prevIsLoadingRef.current = isLoading;
      lastContentRef.current = lastContent;
    }
  });

  // Also use useEffect as a backup for async updates (runs after paint)
  useEffect(() => {
    // Small delay to ensure DOM is fully updated, especially for content updates
    const timeoutId = setTimeout(() => {
      performScroll();
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [chatData.length, isLoading, performScroll]);
  const handlePdfDownload = (base64Pdf?: string | null) => {
    const linkSource = `data:application/pdf;base64,${base64Pdf}`;
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = 'permit-form.pdf';
    downloadLink.click();
  };

  const renderMessageContent = useMemo(() => {
    return (content: string) => {
      const normalized = content ? content.replace(/<br\s*\/?>/gi, '\n') : '';
      return (
        <ReactMarkdown
          linkTarget="_blank"
          components={{
            a: ({ node, children, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
            p: ({ node, children, ...props }) => {
              const plainText = React.Children.toArray(children)
                .map((child) => (typeof child === 'string' ? child : ''))
                .join('')
                .trim();
              const isHeadingLike = plainText.endsWith(':');
              return (
                <p {...props} className={isHeadingLike ? 'permit-md-heading-line' : undefined}>
                  {children}
                </p>
              );
            },
          }}
        >
          {normalized}
        </ReactMarkdown>
      );
    };
  }, []);

  return (
    <div ref={chatListRef} className={`chat-list ${chatData ? 'full-screen' : ''}`}>
      {chatData.map((item, index) => (
        <div
          key={index}
          ref={index === chatData.length - 1 && !isLoading ? lastMessageRef : undefined}
          className={`chat-message ${item.role === 'user' ? 'user' : 'assistant'}`}
        >
          <div className="message-content">
            {item.role === 'user' && item.attachFiles && item.attachFiles.length > 0 && (
              <div className="d-flex mb-2 overflow-auto">
                {item.attachFiles.map((file: any, fileIndex: number) => {
                  return IMAGE_MIME_TYPES.includes(file.type) ? (
                    <div className="preview-img mr-1" key={fileIndex}>
                      <img src={file.preview} alt={file.name} />
                    </div>
                  ) : (
                    <div className="preview-file" key={fileIndex}>
                      <FileCopy color="primary" />
                      <div>
                        <p>{file.name}</p>
                        <p>{file.type}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {renderMessageContent(item.content)}

          <div className="button-container">
            {item.pdf && (
              <div className="pdf-download" onClick={() => handlePdfDownload(item.pdf)}>
                    <i className="fas fa-file-pdf"></i>
                    <span>Download PDF  <CloudDownload color="primary"/></span>
                  </div>
            )}

            {item.status === 'idle' && (
                <button className="btn research-Btn" onClick={onStartResearch}>
                  Please go ahead
                </button>
            )}
              </div>
          </div>
        </div>
      ))}
      {isLoading && (
        <div ref={lastMessageRef} className="chat-message assistant">
          <div className="message-content loading-message">
            <div className="spinner"></div>
            <p>Generating response...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
